import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, Image as ImageIcon, Loader2, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { apiFetch } from "../lib/api.js";
import { invalidateActiveMe } from "../lib/use-active-org.js";

interface OrgData {
  id: string;
  name: string;
  slug: string;
  logo: string | null;
  createdAt?: string;
}
interface OrgResponse {
  ok: true;
  organization: OrgData;
  role: string;
}
interface Storage {
  ok: true;
  orgId: string;
  used: number;
  limit: number;
  remaining: number;
}
interface PresignResponse {
  url: string;
  s3Key: string;
  headers: Record<string, string>;
  publicUrl: string;
  expiresIn: number;
}

const LOGO_MAX_BYTES = 4 * 1024 * 1024;
const LOGO_ALLOWED = /^image\/(png|jpe?g|webp|svg\+xml|avif)$/i;

/**
 * Verein (club) page.
 *
 * Mirrors the Konto page styling. Three cards:
 *
 *   1. Verein     — logo / Wappen + name. Editable only when the current
 *                   user has the `owner` role on the active org; everyone
 *                   else sees a read-only view.
 *   2. Speicher   — per-club storage quota (moved from the Konto page so
 *                   storage lives next to the club it belongs to).
 *   3. Dashboard  — external link to the web dashboard for coach- and
 *                   licence-management. Owner-only — hidden for coaches.
 */
export function ClubPage() {
  const qc = useQueryClient();

  const org = useQuery({
    queryKey: ["org"],
    queryFn: () => apiFetch<OrgResponse>("/api/v1/org"),
  });

  const storage = useQuery({
    queryKey: ["me", "storage"],
    queryFn: () => apiFetch<Storage>("/api/v1/me/storage"),
    enabled: !!org.data?.organization,
  });

  const patchOrg = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiFetch<{ ok: boolean; organization: OrgData | null }>("/api/v1/org", {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      invalidateActiveMe();
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["org"] }),
        qc.invalidateQueries({ queryKey: ["me"] }),
        qc.invalidateQueries({ queryKey: ["me", "storage"] }),
      ]);
    },
  });

  const isOwner = org.data?.role === "owner";

  return (
    <div className="flex flex-col gap-4">
      <ClubCard
        org={org.data?.organization}
        loading={org.isLoading}
        canEdit={isOwner}
        saving={patchOrg.isPending}
        onSave={(patch) => patchOrg.mutateAsync(patch)}
        onChangeLogo={async (logo) => {
          await patchOrg.mutateAsync({ logoUpload: logo });
        }}
        onClearLogo={() => patchOrg.mutateAsync({ logo: null })}
      />
      <StorageCard storage={storage.data} loading={storage.isLoading} />
      {isOwner && <DashboardLinkCard />}
    </div>
  );
}

/* ──────────────────────── Club identity card ───────────────────────── */

function ClubCard({
  org,
  loading,
  canEdit,
  saving,
  onSave,
  onChangeLogo,
  onClearLogo,
}: {
  org: OrgData | undefined;
  loading: boolean;
  canEdit: boolean;
  saving: boolean;
  onSave: (patch: { name?: string }) => Promise<unknown>;
  onChangeLogo: (logo: {
    s3Key: string;
    publicUrl: string;
    bytes: number;
    mime: string;
  }) => Promise<unknown>;
  onClearLogo: () => Promise<unknown>;
}) {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [name, setName] = useState("");
  const [hydrated, setHydrated] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(null);

  // Hydrate the edit buffer from the server snapshot once. Avoids stomping
  // an in-progress edit when a background refetch lands.
  useEffect(() => {
    if (org && !hydrated) {
      setName(org.name ?? "");
      setHydrated(true);
    }
  }, [org, hydrated]);

  const initials = computeOrgInitials(org?.name ?? null);
  const trimmedName = name.trim();
  const nameDirty = !!org && trimmedName !== (org.name ?? "").trim() && trimmedName.length > 0;
  const dirty = canEdit && nameDirty;

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!LOGO_ALLOWED.test(file.type)) {
      setMsg({ kind: "err", text: "Nur PNG, JPG, WebP, SVG oder AVIF." });
      return;
    }
    if (file.size > LOGO_MAX_BYTES) {
      setMsg({ kind: "err", text: "Maximal 4 MB." });
      return;
    }

    setUploadBusy(true);
    setMsg(null);
    try {
      const presign = await apiFetch<PresignResponse>("/api/v1/media/org-logo-upload-url", {
        method: "POST",
        body: JSON.stringify({ mime: file.type, bytes: file.size }),
      });

      const put = await fetch(presign.url, {
        method: "PUT",
        headers: presign.headers,
        body: file,
      });
      if (!put.ok) throw new Error(`PUT failed: ${put.status}`);

      await onChangeLogo({
        s3Key: presign.s3Key,
        publicUrl: presign.publicUrl,
        bytes: file.size,
        mime: file.type,
      });
      setMsg({ kind: "ok", text: "Vereinslogo aktualisiert." });
    } catch (err) {
      const text =
        err instanceof Error && err.message.includes("413")
          ? "Speicherlimit erreicht. Lösche zuerst andere Medien."
          : "Upload fehlgeschlagen.";
      setMsg({ kind: "err", text });
    } finally {
      setUploadBusy(false);
    }
  }

  return (
    <Card>
      <div className="flex items-baseline justify-between">
        <h2 className="home-display text-[18px] font-bold" style={{ color: "#f4f5f3" }}>
          Verein
        </h2>
        {!canEdit && org && (
          <span
            className="text-[11px] uppercase tracking-[0.14em]"
            style={{ color: "var(--color-on-surface-variant)" }}
          >
            Nur Lesen
          </span>
        )}
      </div>

      <div className="mt-6 flex flex-col items-center gap-6 sm:flex-row sm:items-center sm:gap-7">
        <div className="relative shrink-0">
          <div
            className="grid size-24 place-items-center overflow-hidden rounded-2xl"
            style={{
              background: org?.logo
                ? "transparent"
                : "linear-gradient(140deg, rgba(151,252,0,0.45) 0%, rgba(151,252,0,0.10) 100%)",
              border: "1px solid rgba(255,255,255,0.10)",
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.20)",
            }}
          >
            {org?.logo ? (
              <img src={org.logo} alt="" className="size-24 rounded-2xl object-cover" />
            ) : (
              <span
                className="home-display"
                style={{ fontSize: 28, fontWeight: 800, color: "#1d3700" }}
              >
                {initials}
              </span>
            )}
          </div>

          {canEdit && (
            <>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploadBusy || saving}
                aria-label="Vereinslogo ändern"
                className="absolute bottom-0 right-0 grid size-9 place-items-center rounded-full border transition-colors duration-fast disabled:opacity-60"
                style={{
                  background: "var(--color-pitch-lime)",
                  color: "#1d3700",
                  borderColor: "rgba(10,12,8,0.45)",
                  boxShadow: "0 6px 16px -4px rgba(151,252,0,0.45)",
                }}
              >
                {uploadBusy ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <ImageIcon className="size-4" />
                )}
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml,image/avif"
                className="hidden"
                onChange={handleFile}
              />
            </>
          )}
        </div>

        <div className="min-w-0 flex-1 self-stretch">
          <label
            htmlFor="club-name"
            className="text-[11px] uppercase tracking-[0.14em]"
            style={{ color: "var(--color-on-surface-variant)" }}
          >
            Vereinsname
          </label>
          {canEdit ? (
            <input
              id="club-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={loading ? "…" : "Vereinsname"}
              maxLength={120}
              className="mt-2 w-full rounded-xl border bg-transparent px-3.5 py-2.5 text-[15px] outline-none transition-colors focus:border-pitch-lime"
              style={{
                borderColor: "rgba(255,255,255,0.12)",
                color: "#f4f5f3",
              }}
            />
          ) : (
            <p id="club-name" className="mt-2 truncate text-[15px]" style={{ color: "#f4f5f3" }}>
              {org?.name ?? "—"}
            </p>
          )}
          {org?.slug && (
            <p
              className="mt-3 text-[11px] uppercase tracking-[0.14em]"
              style={{ color: "var(--color-on-surface-variant)" }}
            >
              Kennung: <span style={{ color: "#f4f5f3" }}>{org.slug}</span>
            </p>
          )}
        </div>
      </div>

      {canEdit && (
        <div className="mt-6 flex flex-wrap items-center justify-end gap-2">
          {org?.logo && (
            <button
              type="button"
              onClick={() => {
                setMsg(null);
                void onClearLogo().then(() =>
                  setMsg({ kind: "ok", text: "Vereinslogo entfernt." }),
                );
              }}
              disabled={saving || uploadBusy}
              className="inline-flex items-center gap-1.5 rounded-xl border px-3 py-2 text-[13px] transition-colors disabled:opacity-60"
              style={{
                borderColor: "rgba(255,255,255,0.10)",
                color: "var(--color-on-surface-variant)",
              }}
            >
              <Trash2 className="size-4" /> Logo entfernen
            </button>
          )}
          <button
            type="button"
            disabled={!dirty || saving}
            onClick={() => {
              setMsg(null);
              const patch: { name?: string } = {};
              if (nameDirty) patch.name = trimmedName;
              void onSave(patch).then(() =>
                setMsg({ kind: "ok", text: "Vereinsdaten gespeichert." }),
              );
            }}
            className="inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-[13px] font-semibold transition-colors disabled:opacity-50"
            style={{
              background: "var(--color-pitch-lime)",
              color: "#1d3700",
            }}
          >
            {saving ? <Loader2 className="size-4 animate-spin" /> : null}
            Speichern
          </button>
        </div>
      )}

      {msg && (
        <p
          className="mt-3 text-[12px]"
          style={{
            color: msg.kind === "ok" ? "var(--color-pitch-lime)" : "var(--color-error, #ff6b6b)",
          }}
        >
          {msg.text}
        </p>
      )}
    </Card>
  );
}

/* ──────────────────────── Storage card ─────────────────────────── */

function StorageCard({ storage, loading }: { storage: Storage | undefined; loading: boolean }) {
  const used = storage?.used ?? 0;
  const limit = storage?.limit ?? 0;
  const pct = limit > 0 ? Math.min(100, (used / limit) * 100) : 0;
  const overHalf = pct >= 50;
  const overNinety = pct >= 90;

  return (
    <Card>
      <div className="flex items-baseline justify-between">
        <h2 className="home-display text-[18px] font-bold" style={{ color: "#f4f5f3" }}>
          Speicher
        </h2>
        <span
          className="text-[11px] uppercase tracking-[0.14em]"
          style={{ color: "var(--color-on-surface-variant)" }}
        >
          {loading ? "…" : `${formatBytes(used)} / ${formatBytes(limit)}`}
        </span>
      </div>

      <div
        className="mt-4 h-2 w-full overflow-hidden rounded-full"
        style={{ background: "rgba(255,255,255,0.08)" }}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(pct)}
      >
        <div
          className="h-full rounded-full transition-[width] duration-500"
          style={{
            width: `${pct}%`,
            background: overNinety
              ? "linear-gradient(90deg, #ffb347 0%, #ff6b6b 100%)"
              : overHalf
                ? "linear-gradient(90deg, var(--color-pitch-lime) 0%, #ffb347 100%)"
                : "var(--color-pitch-lime)",
          }}
        />
      </div>

      <p
        className="mt-3 text-[12px] leading-relaxed"
        style={{ color: "var(--color-on-surface-variant)" }}
      >
        Dieser Speicher ist pro Verein begrenzt und umfasst Profilbilder, Spielerfotos, Vereinslogos
        und Taktikboard-Snapshots.
      </p>
    </Card>
  );
}

/* ─────────────────── Dashboard link card (owner) ───────────────────── */

function DashboardLinkCard() {
  const url = import.meta.env.VITE_PUBLIC_LANDING_URL ?? "http://localhost:28781";
  return (
    <Card>
      <h2 className="home-display text-[18px] font-bold" style={{ color: "#f4f5f3" }}>
        Trainer- &amp; Lizenzverwaltung
      </h2>
      <p
        className="mt-2 text-[13px] leading-relaxed"
        style={{ color: "var(--color-on-surface-variant)" }}
      >
        Trainer einladen, Rollen vergeben und Lizenzen verwalten erfolgt im Web-Dashboard. Hier
        öffnest du es im Browser.
      </p>
      <div className="mt-5 flex justify-end">
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-[13px] font-semibold transition"
          style={{
            background: "var(--color-pitch-lime)",
            color: "#1d3700",
          }}
        >
          Dashboard öffnen
          <ExternalLink className="size-4" />
        </a>
      </div>
    </Card>
  );
}

/* ───────────────────────── building blocks ─────────────────────── */

function Card({ children }: { children: React.ReactNode }) {
  return (
    <section className="glass-floating rounded-3xl px-6 py-7 sm:px-8 sm:py-8">{children}</section>
  );
}

function computeOrgInitials(name: string | null): string {
  const source = (name?.trim() || "?").trim();
  const parts = source.split(/\s+/).filter(Boolean).slice(0, 2);
  const letters = parts.map((p) => p[0]?.toUpperCase() ?? "").join("");
  return letters || source[0]?.toUpperCase() || "?";
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v >= 100 ? 0 : v >= 10 ? 1 : 2)} ${units[i]}`;
}
